import React, { useState, useEffect } from 'react';
import { Star, MapPin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from './OptimizedImage';
import { collegeService } from '../lib/database';
import type { College } from '../lib/supabase';

const TopReviews = () => {
  const [colleges, setColleges] = useState<College[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopColleges();
  }, []);

  const fetchTopColleges = async () => {
    try {
      const data = await collegeService.getFeatured(4);
      setColleges(data);
    } catch (error) {
      console.error('Error fetching top colleges:', error);
      setColleges([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (colleges.length === 0) {
    return (
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold text-dark mb-3">
              Top College Reviews
            </h2>
            <p className="lead text-muted">
              Real reviews from real students
            </p>
          </div>
          <div className="text-center">
            <p className="text-muted">No colleges available yet.</p>
            <Link to="/college-reviews" className="btn btn-primary">
              View All Colleges
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-3 py-md-5 bg-light">
      <div className="container">
        <div className="text-center mb-4 mb-md-5 px-2">
          <h2 className="h1 fw-bold text-dark mb-3" style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)' }}>
            Top College Reviews
          </h2>
          <p className="lead text-muted" style={{ fontSize: 'clamp(1rem, 3vw, 1.25rem)' }}>
            Real reviews from real students
          </p>
        </div>

        <div className="row g-2 g-md-4 px-2 px-md-0">
          {colleges.map((college) => (
            <div key={college.id} className="col-12 col-sm-6 col-lg-3">
              <div className="college-card card h-100">
                <div className="position-relative">
                  <OptimizedImage
                    src={college.image_url}
                    alt={`${college.name} campus - Top rated ${college.category} college in ${college.location}`}
                    title={`${college.name} | Rating: ${college.rating}/5 | ${college.type} College`}
                    className="card-img-top"
                    style={{ height: 'clamp(140px, 20vw, 200px)', objectFit: 'cover' }}
                    loading="lazy"
                    fallbackSrc="https://images.pexels.com/photos/1454360/pexels-photo-1454360.jpeg?auto=compress&cs=tinysrgb&w=400"
                  />
                  <span className="category-badge position-absolute top-0 end-0 m-3">
                    {college.category}
                  </span>
                </div>

                <div className="card-body p-3 p-md-4">
                  <h5 className="card-title fw-bold text-dark mb-2" style={{ fontSize: 'clamp(1rem, 3.5vw, 1.25rem)' }}>
                    {college.name}
                  </h5>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-1">
                      <Star className="star-rating" size={16} fill="currentColor" />
                      <span className="fw-semibold text-dark">{college.rating}</span>
                    </div>
                    
                    <div className="d-flex align-items-center gap-1 text-muted">
                      <MapPin size={14} />
                      <small>{college.location}</small>
                    </div>
                  </div>

                  <p className="card-text text-muted line-clamp-3 mb-3" style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.875rem)' }}>
                    {college.description}
                  </p>

                  <Link 
                    to={`/college/${college.slug}`}
                    className="btn btn-link p-0 text-primary fw-semibold d-flex align-items-center gap-2"
                    style={{ fontSize: 'clamp(0.8rem, 2.5vw, 0.875rem)' }}
                    aria-label={`Read detailed review of ${college.name}`}
                  >
                    <span>Read Review</span>
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-4 mt-md-5 px-2">
          <Link 
            to="/college-reviews" 
            className="btn btn-primary btn-lg w-100 w-sm-auto"
            style={{ minHeight: '48px', maxWidth: '300px' }}
            aria-label="View all college reviews and ratings"
          >
            View All Reviews
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TopReviews;